import React, 
       { useEffect, 
         useRef,
         useCallback 
       }                                    from 'react'
import { Box,
         TextField,
         FormControl,
         Select,
         MenuItem,
         FormControlLabel,
         Checkbox,
         Typography,
         Grid,
         useTheme,
         darken,
       }                                    from '@mui/material'
import { useForm, 
         Controller 
       }                                    from 'react-hook-form'
import { APIData }                          from 'types/cms/frontendTypes'
import { CreateSurveyQuestionRequestData, 
         SurveyQuestionLocalization 
       }                                    from 'types/apis/surveyInterfaces'
import { AnswerChoicesSection }             from './AnswerChoicesSection'
import { AnswerChoice }                     from 'types/apis/surveyInterfaces'
import { SurveyQBUtils }                    from '../utils/SurveyQBUtils'
import { isEqual }                          from 'lodash'
import { useSelector, useDispatch }         from 'react-redux'
import { selectCMSSelectedPilot, 
         selectCMSLocales, 
         CMS_DEFAULT_LOCALE 
       }                                    from 'store/dataReducers/cmsPilotSlice'
import { selectSurveyCategories, selectTableState, 
         setSelectedLocaleAction 
       }                                    from 'store/dataReducers/surveyQBSlice'

export interface SurveyQuestionFormProps {
  question?: APIData<'api::survey-question.survey-question'>
  publishedQuestion?: APIData<'api::survey-question.survey-question'>
  isReadOnly: boolean
  onFormStateChange: (isDirty: boolean) => void
  onSubmit: (data: any) => void
  onFormReady?: (submitFn: () => void) => void
}

const QUESTION_TYPES = [
  'SINGLE',
  'MULTIPLE',
  'MULTIPLE_WITH_SUB_CHOICES',
  'TEXT',
  'NUMBER',
  'IMAGE_UPLOAD',
  'TEXTAREA',
  'BOOLEAN',
  'RANGE',
  'SLIDER',
  'TWO_DIMENSIONS',
  'THREE_DIMENSIONS',
] as const

const GROUPS = ['ESSENTIAL', 'DETAILED'] as const
const PROFILE_CODES = ['AP', 'HP', 'METADATA'] as const
const FUEL_TYPES = ['ELECTRIC', 'GAS', 'WATER'] as const

const defaultFormValues = {
  title: '',
  info: '',
  type: 'SINGLE',
  group: 'DETAILED',
  category: '',
  profileCode: 'METADATA',
  stateMapper: '',
  fuelTypes: ['GLOBAL'] as string[],
  impactShc: false,
  impactItemization: false,
  impactReco: false,
  choices: [],
  localizations: [] as SurveyQuestionLocalization[],
} as unknown as Partial<CreateSurveyQuestionRequestData>

export const SurveyQuestionForm: React.FC<SurveyQuestionFormProps> = ({
  question,
  publishedQuestion,
  isReadOnly,
  onFormStateChange,
  onSubmit,
  onFormReady,
}) => {

  const formRef = useRef<HTMLFormElement>(null)
  const theme = useTheme()
  const dispatch = useDispatch()

  const selectedPilot = useSelector(selectCMSSelectedPilot)
  const tableState    = useSelector(selectTableState)
  const locales       = useSelector(selectCMSLocales)!
  const categories    = useSelector(selectSurveyCategories)!

  const selectedLocale = tableState.selectedLocale

  const { control, handleSubmit, formState: { errors, isDirty }, reset, watch, setValue, getValues, trigger, setError } = useForm({
    defaultValues: defaultFormValues,
    mode: 'onChange',
  } as any) as any

  // Store handleSubmit, trigger, and onFormSubmit in refs to avoid stale closures
  const handleSubmitRef = useRef(handleSubmit)
  const triggerRef = useRef(trigger)
  const onFormSubmitRef = useRef<((data: any) => void) | null>(null)
  
  // Keep refs updated with latest values on every render
  handleSubmitRef.current = handleSubmit
  triggerRef.current = trigger

  const questionType = watch('type')

  const [isGlobal, setIsGlobal] = React.useState<boolean>(false)
  const [selectedFuelTypes, setSelectedFuelTypes] = React.useState<string[]>([])
  const [answerChoices, setAnswerChoices] = React.useState<AnswerChoice[]>([])

  // Helper function to get locale-specific value for top-level fields
  const getLocaleValue = (question: APIData<'api::survey-question.survey-question'>, fieldName: 'title' | 'info', locale: string): string => {
    if (locale === CMS_DEFAULT_LOCALE) {
      return (question[fieldName] as string) || '';
    }
    
    const localizations: SurveyQuestionLocalization[] = (question as any).localizations || [];
    const localeData = localizations.find((l: SurveyQuestionLocalization) => l.locale === locale) || {} as SurveyQuestionLocalization;
    return (localeData[fieldName] as string) || '';
  };

  // Helper function to get locale-specific choice title
  const getChoiceLocaleTitle = (choice: any, choiceIndex: number, locale: string, question: APIData<'api::survey-question.survey-question'>): string => {
    if (locale === CMS_DEFAULT_LOCALE) {
      return choice.title || '';
    }
    
    const localizations: SurveyQuestionLocalization[] = (question as any).localizations || [];
    const localeData = localizations.find((l: SurveyQuestionLocalization) => l.locale === locale);
    if (localeData && localeData.choices && localeData.choices[choiceIndex]) {
      return localeData.choices[choiceIndex].title || '';
    }
    return '';
  };

  // Helper function to get locale-specific subchoice title
  const getSubChoiceLocaleTitle = (subChoice: any, choiceIndex: number, subChoiceIndex: number, locale: string, question: APIData<'api::survey-question.survey-question'>): string => {
    if (locale === CMS_DEFAULT_LOCALE) {
      return subChoice.title || '';
    }
    
    const localizations: SurveyQuestionLocalization[] = (question as any).localizations || [];
    const localeData = localizations.find((l: SurveyQuestionLocalization) => l.locale === locale);
    if (localeData && localeData.choices && localeData.choices[choiceIndex] && localeData.choices[choiceIndex].subChoices && localeData.choices[choiceIndex].subChoices![subChoiceIndex]) {
      return localeData.choices[choiceIndex].subChoices![subChoiceIndex].title || '';
    }
    return '';
  };

  // Helper function to set locale-specific value and update localizations in form state
  const setLocaleTextValue = (fieldName: 'title' | 'info', value: string) => {
    // Always update the form field so user sees their changes immediately
    setValue(fieldName, value, { shouldDirty: true, shouldValidate: true });
    
    if (selectedLocale === CMS_DEFAULT_LOCALE) {
      // Default locale - form field update is sufficient
      return;
    }
    
    // Non-default locale - also update localizations in form state
    const allValues = getValues();
    const existingLocalizations: SurveyQuestionLocalization[] = allValues.localizations || [];
    const existingIndex = existingLocalizations.findIndex((l: SurveyQuestionLocalization) => l.locale === selectedLocale);

    let updatedLocalizations: SurveyQuestionLocalization[];
    if (existingIndex >= 0) {
      // Update existing locale entry
      const existing = existingLocalizations[existingIndex];
      const updated: SurveyQuestionLocalization = {
        ...existing,
        [fieldName]: value,
      };
      updatedLocalizations = [
        ...existingLocalizations.slice(0, existingIndex),
        updated,
        ...existingLocalizations.slice(existingIndex + 1)
      ];
    } else {
      // Create new locale entry
      const newLocaleEntry: SurveyQuestionLocalization = {
        locale: selectedLocale,
        title: fieldName === 'title' ? value : (allValues.title || ''),
        ...(fieldName === 'info' ? { info: value } : {}),
        choices: [],
      };
      updatedLocalizations = [...existingLocalizations, newLocaleEntry];
    }

    setValue('localizations', updatedLocalizations, { shouldDirty: true, shouldValidate: true });
  };

  // Helper function to set locale-specific choices and update localizations in form state
  const setLocaleChoicesValue = (choices: AnswerChoice[]) => {
    if (selectedLocale === CMS_DEFAULT_LOCALE) {
      // Default locale - choices are stored in main choices field
      // This is handled by handleChoicesChange
      return;
    }
    
    // Non-default locale - update localizations[].choices in form state
    const allValues = getValues();
    const existingLocalizations: SurveyQuestionLocalization[] = allValues.localizations || [];
    const existingIndex = existingLocalizations.findIndex((l: SurveyQuestionLocalization) => l.locale === selectedLocale);

    // Convert choices to localized format (without order, readMapper, etc.)
    const localizedChoices = choices
      .filter(choice => choice.value.trim() !== '' && choice.title.trim() !== '')
      .map((choice) => {
        const localizedSubChoices = (choice.subChoices || [])
          .filter((sc: any) => sc.value && sc.value.trim() !== '')
          .map((subChoice: any) => ({
            title: subChoice.title || '',
            value: subChoice.value || '',
            selectionType: subChoice.selectionType,
            subChoiceValidationType: subChoice.subChoiceValidationType,
            allowedValues: subChoice.allowedValues,
          }));

        return {
          title: choice.title || '',
          value: choice.value || '',
          ...(localizedSubChoices.length > 0 ? { subChoices: localizedSubChoices } : {}),
        };
      });

    let updatedLocalizations: SurveyQuestionLocalization[];
    if (existingIndex >= 0) {
      // Update existing locale entry
      const existing = existingLocalizations[existingIndex];
      const updated: SurveyQuestionLocalization = {
        ...existing,
        choices: localizedChoices,
      };
      updatedLocalizations = [
        ...existingLocalizations.slice(0, existingIndex),
        updated,
        ...existingLocalizations.slice(existingIndex + 1)
      ];
    } else {
      // Create new locale entry
      const newLocaleEntry: SurveyQuestionLocalization = {
        locale: selectedLocale,
        title: allValues.title || '',
        ...(allValues.info ? { info: allValues.info } : {}),
        choices: localizedChoices,
      };
      updatedLocalizations = [...existingLocalizations, newLocaleEntry];
    }

    setValue('localizations', updatedLocalizations, { shouldDirty: true, shouldValidate: true });
  };

  // Handle locale change
  const handleLocaleChange = (newLocale: string) => {
    dispatch(setSelectedLocaleAction(newLocale));
  };

  // Initialize form when question data is provided
  useEffect(() => {
    if (question) {
      // Parse fuel type - if it's GLOBAL, set isGlobal to true, otherwise parse comma-separated
      const fuelTypes = (question.fuelTypes || []) as string[]
      if (fuelTypes.includes('GLOBAL')) {
        setIsGlobal(true)
        setSelectedFuelTypes([])
      } else {
        setIsGlobal(false)
        setSelectedFuelTypes(fuelTypes)
      }

      // Convert choices to AnswerChoice format with locale-specific titles
      const choices: AnswerChoice[] = (question.choices || []).map((choice: any, index: number) => ({
        title: getChoiceLocaleTitle(choice, index, selectedLocale, question) || choice.title || '',
        value: choice.value,
        order: choice.order,
        readMapper: choice.readMapper,
        writeMapper: choice.writeMapper,
        unselectMapper: choice.unselectMapper,
        subChoices: (choice.subChoices || []).map((subChoice: any, subIndex: number) => ({
          ...subChoice,
          title: getSubChoiceLocaleTitle(subChoice, index, subIndex, selectedLocale, question) || subChoice.title || '',
        })),
      }))

      setAnswerChoices(choices)

      reset({
        title: getLocaleValue(question, 'title', selectedLocale),
        type: question.type,
        group: question.group,
        category: question.category,
        profileCode: question.profileCode,
        stateMapper: question.stateMapper,
        fuelTypes: question.fuelTypes,
        impactShc: question.impactShc,
        impactItemization: question.impactItemization,
        impactReco: question.impactReco,
        choices: choices,
        verificationStatus: question.verificationStatus,
        info: getLocaleValue(question, 'info', selectedLocale),
        localizations: question.localizations || [],
      })
      } else {
       reset(defaultFormValues)
       setIsGlobal(true)
       setSelectedFuelTypes([])
       setAnswerChoices([])
     }
   }, [question, reset, selectedLocale])

  // Track form changes and notify parent
  useEffect(() => {
    onFormStateChange?.(isDirty)
  }, [isDirty, onFormStateChange])

  const handleGlobalChange = (checked: boolean) => {
    setIsGlobal(checked)
    if (checked) {
      setSelectedFuelTypes([])
      setValue('fuelTypes', ['GLOBAL'] as string[], { shouldDirty: true })
    }
  }

  const handleFuelTypeChange = (fuelType: string, checked: boolean) => {
    if (isGlobal) {
      // If GLOBAL is selected, uncheck it when selecting a specific fuel type
      setIsGlobal(false)
    }

    let newSelected: string[]
    if (checked) {
      newSelected = [...selectedFuelTypes, fuelType]
    } else {
      newSelected = selectedFuelTypes.filter(ft => ft !== fuelType)
    }

    setSelectedFuelTypes(newSelected)

    // Store as comma-separated string (never empty - if empty, should be GLOBAL)
    const fuelTypes = newSelected.length === 0 ? ['GLOBAL'] : newSelected
    setValue('fuelTypes', fuelTypes as string[], { shouldDirty: true })
  }

  const handleChoicesChange = (choices: AnswerChoice[]) => {
    setAnswerChoices(choices)
    
    if (selectedLocale === CMS_DEFAULT_LOCALE) {
      // Default locale - update form choices field directly
      setValue('choices', choices as any, { shouldDirty: true })
    } else {
      // Non-default locale - update localizations in form state
      setLocaleChoicesValue(choices)
      // Also update form choices field for display (though it won't be used on submit)
      setValue('choices', choices as any, { shouldDirty: true })
    }
  }

  // Update form fields and choices when locale changes to show locale-specific values
  useEffect(() => {
    if (question) {
      // Get locale-specific values - check form state localizations first (for unsaved edits),
      // then fall back to question's localizations
      const formValues = getValues();
      const formLocalizations = formValues.localizations || [];
      const formLocaleData = formLocalizations.find((l: SurveyQuestionLocalization) => l.locale === selectedLocale);
      
      // Get title - prefer form state, then question
      const titleValue = selectedLocale === CMS_DEFAULT_LOCALE
        ? (formValues.title || getLocaleValue(question, 'title', selectedLocale))
        : (formLocaleData?.title || getLocaleValue(question, 'title', selectedLocale));
      
      // Get info - prefer form state, then question
      const infoValue = selectedLocale === CMS_DEFAULT_LOCALE
        ? (formValues.info || getLocaleValue(question, 'info', selectedLocale))
        : (formLocaleData?.info || getLocaleValue(question, 'info', selectedLocale));
      
      setValue('title', titleValue, { shouldDirty: false })
      setValue('info', infoValue, { shouldDirty: false })
      
      // Update choices with locale-specific titles
      if (answerChoices.length > 0) {
        const updatedChoices = answerChoices.map((choice, index) => {
          // For non-default locale, check form state localizations first
          let choiceTitle = choice.title;
          if (selectedLocale !== CMS_DEFAULT_LOCALE && formLocaleData?.choices?.[index]) {
            choiceTitle = formLocaleData.choices[index].title || choiceTitle;
          } else {
            choiceTitle = getChoiceLocaleTitle(choice, index, selectedLocale, question) || choiceTitle;
          }
          
          const updatedSubChoices = (choice.subChoices || []).map((subChoice: any, subIndex: number) => {
            let subChoiceTitle = subChoice.title;
            if (selectedLocale !== CMS_DEFAULT_LOCALE && formLocaleData?.choices?.[index]?.subChoices?.[subIndex]) {
              subChoiceTitle = formLocaleData.choices[index].subChoices![subIndex].title || subChoiceTitle;
            } else {
              subChoiceTitle = getSubChoiceLocaleTitle(subChoice, index, subIndex, selectedLocale, question) || subChoiceTitle;
            }
            return {
              ...subChoice,
              title: subChoiceTitle,
            };
          });
          
          return {
            ...choice,
            title: choiceTitle,
            subChoices: updatedSubChoices,
          }
        })
        setAnswerChoices(updatedChoices)
        setValue('choices', updatedChoices as any, { shouldDirty: false })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocale])

  const shouldShowChoices = ['SINGLE', 'MULTIPLE', 'MULTIPLE_WITH_SUB_CHOICES'].includes(questionType)

  // Validate choices before submission
  // Only validate if choices exist - allow questions without choices
  const validateChoices = useCallback((): boolean => {
    if (shouldShowChoices && answerChoices.length > 0) {
      // If choices exist, validate they are complete
      return answerChoices.every(choice => choice.title.trim() !== '' && choice.value.trim() !== '')
    }
    // No choices or not a choice-based question type - validation passes
    return true
  }, [shouldShowChoices, answerChoices])

  const onFormSubmit = useCallback((data: any) => {
    // Validate choices before submission
    // Only validate if choices exist - allow questions without choices
    const choicesValid = validateChoices()
    if (!choicesValid) {
      // Set error on choices field to show user
      // This only happens if choices exist but are incomplete
      if (shouldShowChoices && answerChoices.length > 0) {
        setError('choices', { 
          type: 'manual', 
          message: 'All answer choices must have both title and value' 
        }, { shouldFocus: true })
      }
      return
    }

    // Get all form values (following RecoEditFormComponent pattern)
    const allValues = getValues() as CreateSurveyQuestionRequestData

    // Get default locale choices
    // - Default locale: use answerChoices (already has default locale titles)
    // - Non-default locale (edit mode): use question.choices (default locale from question)
    // - Non-default locale (create mode): use answerChoices (user's input, treat as default locale)
    const getDefaultLocaleChoices = (): AnswerChoice[] => {
      if (selectedLocale === CMS_DEFAULT_LOCALE) {
        return answerChoices
      } else {
        // For non-default locale, use question's choices if editing, otherwise use answerChoices
        return (question?.choices && question.choices.length > 0) 
          ? question.choices.map((choice: any, index: number) => ({
              title: choice.title || '',
              value: choice.value || '',
              order: choice.order ?? index + 1, // Use ?? to preserve 0 values
              readMapper: choice.readMapper,
              writeMapper: choice.writeMapper,
              unselectMapper: choice.unselectMapper,
              subChoices: (choice.subChoices || []).map((sc: any, scIndex: number) => ({
                title: sc.title || '',
                value: sc.value || '',
                order: sc.order ?? scIndex + 1, // Use ?? to preserve 0 values
                selectionType: sc.selectionType,
                subChoiceValidationType: sc.subChoiceValidationType,
                allowedValues: sc.allowedValues,
              })),
            }))
          : answerChoices // Create mode - use user's input
      }
    }

    // Build default locale choices - filter and ensure proper structure
    // Normalize to match the structure expected by the diff utility
    const defaultLocaleChoices = getDefaultLocaleChoices()
      .filter(choice => {
        // Filter out invalid choices
        if (choice.value.trim() === '') return false
        // For default locale, also require title
        if (selectedLocale === CMS_DEFAULT_LOCALE && choice.title.trim() === '') return false
        return true
      })
      .map((choice, index) => ({
        title: choice.title || '',
        value: choice.value || '',
        readMapper: choice.readMapper,
        writeMapper: choice.writeMapper,
        unselectMapper: choice.unselectMapper,
        order: choice.order ?? index + 1, // Use ?? to preserve 0 values, normalize undefined/null
        subChoices: (choice.subChoices || []).map((sc: any, scIndex: number) => ({
          title: sc.title || '',
          value: sc.value || '',
          order: sc.order ?? scIndex + 1, // Use ?? to preserve 0 values, normalize undefined/null
          selectionType: sc.selectionType,
          subChoiceValidationType: sc.subChoiceValidationType,
          allowedValues: sc.allowedValues,
        })),
      }))

    // Get localizations from form state (following RecoEditFormComponent pattern)
    // Localizations are already updated in form state via setLocaleTextValue and setLocaleChoicesValue
    const localizations: SurveyQuestionLocalization[] = allValues.localizations || []

    // Build complete form data (following RecoEditFormComponent pattern)
    const formData: any = {
      ...allValues,
      choices: defaultLocaleChoices as any,
      // Only include localizations if they exist and have content
      // This prevents sending empty arrays when nothing changed
      ...(localizations.length > 0 ? { localizations } : {}),
    }

    // Only override title/info for non-default locale (to preserve default locale values from question)
    // For default locale, allValues already has the correct values
    if (selectedLocale !== CMS_DEFAULT_LOCALE) {
      if (question) {
        // Edit mode - preserve default locale values from question
        formData.title = question.title || ''
        formData.info = question.info
      } else {
        // Create mode in non-default locale - set empty defaults
        formData.title = ''
        formData.info = undefined
      }
    }

    onSubmit(formData)
  }, [
    validateChoices,
    shouldShowChoices,
    answerChoices,
    setError,
    getValues,
    selectedLocale,
    question,
    onSubmit
  ])

  // Keep onFormSubmit ref updated whenever the function is recreated
  useEffect(() => {
    onFormSubmitRef.current = onFormSubmit
  }, [onFormSubmit])

  // Expose submit function to parent via callback
  // Recreate submit function whenever onFormReady or onFormSubmit changes
  // This ensures the submit function always has access to the latest onFormSubmit
  useEffect(() => {
    if (onFormReady && onFormSubmitRef.current) {
      const submitFn = async () => {
        if (onFormSubmitRef.current && handleSubmitRef.current) {
          try {
            // Trigger validation first to ensure form is valid
            const isValid = await triggerRef.current()
            
            if (isValid && onFormSubmitRef.current) {
              // handleSubmit returns a function that validates and calls the callback
              const submitHandler = handleSubmitRef.current(
                (data: any) => {
                  onFormSubmitRef.current!(data)
                },
                (errors: any) => {
                  // Validation errors are handled by react-hook-form
                  console.error('Form validation errors:', errors)
                }
              )
              // Call without event for programmatic submission
              submitHandler()
            }
          } catch (error) {
            console.error('Error submitting form:', error)
          }
        }
      }
      onFormReady(submitFn)
    }
  }, [onFormReady, onFormSubmit]) // Recreate when onFormSubmit changes to ensure latest version

  // Get display text for group
  const getGroupDisplayText = (group: string) => {
    return group
  }

  // Common label component for FormControl elements
  const renderFormLabel = (label: string) => (
    <Typography 
      variant="body2" 
      sx={{ 
        mb: 1, 
        color: theme.palette.text.secondary,
        fontWeight: 500,
      }}
    >
      {label}
    </Typography>
  )

  // Add function to check if field is modified (for localized fields)
  const isFieldModified = (fieldName: keyof CreateSurveyQuestionRequestData) => {
    // Only show modifications in edit mode when we have both question and published question
    if (!question || !publishedQuestion) {
      return false;
    }

    // For localized fields (title, info), compare locale-specific values
    if (fieldName === 'title' || fieldName === 'info') {
      const formValues = getValues() as CreateSurveyQuestionRequestData;
      const currentValue = formValues[fieldName] || '';
      const publishedValue = getLocaleValue(publishedQuestion, fieldName as 'title' | 'info', selectedLocale);
      return currentValue !== publishedValue;
    }

    // For non-localized fields, use the standard diff comparison
    const formValues = getValues() as CreateSurveyQuestionRequestData;
    const currentFormData: CreateSurveyQuestionRequestData = {
      ...formValues,
    };

    // Compare current form values with published question to detect modifications
    const diff = SurveyQBUtils.getQuestionDiff(currentFormData, publishedQuestion as any) as any;
    return Object.keys(diff).includes(fieldName);
  }

  // Helper function to check if a specific choice field is modified (handles localization)
  const isChoiceFieldModified = (choiceIndex: number, fieldName: keyof AnswerChoice): boolean => {
    if (!question || !publishedQuestion) {
      return false;
    }

    // For title field, compare locale-specific values
    if (fieldName === 'title') {
      const currentTitle = answerChoices[choiceIndex]?.title || '';
      const publishedTitle = getChoiceLocaleTitle(
        publishedQuestion.choices?.[choiceIndex] || {},
        choiceIndex,
        selectedLocale,
        publishedQuestion
      );
      return currentTitle !== publishedTitle;
    }

    // For other fields, compare directly
    const formValues = getValues() as CreateSurveyQuestionRequestData;
    const currentChoices = formValues.choices || [];
    const publishedChoices = publishedQuestion.choices || [];

    // If choices array length changed, all fields in this choice are considered modified
    if (currentChoices.length !== publishedChoices.length) {
      return choiceIndex < currentChoices.length;
    }

    // If choice at this index doesn't exist in published, it's new/modified
    if (choiceIndex >= publishedChoices.length) {
      return true;
    }

    const currentChoice = currentChoices[choiceIndex];
    const publishedChoice = publishedChoices[choiceIndex];

    if (!currentChoice || !publishedChoice) {
      return false;
    }

    // Compare the specific field
    return !isEqual(currentChoice[fieldName], publishedChoice[fieldName]);
  }

  // Helper function to check if a specific subchoice field is modified (handles localization)
  const isSubChoiceFieldModified = (
    choiceIndex: number,
    subChoiceIndex: number,
    fieldName: keyof import('types/apis/surveyInterfaces').SubChoice
  ): boolean => {
    if (!question || !publishedQuestion) {
      return false;
    }

    // For title field, compare locale-specific values
    if (fieldName === 'title') {
      const currentTitle = answerChoices[choiceIndex]?.subChoices?.[subChoiceIndex]?.title || '';
      const publishedTitle = getSubChoiceLocaleTitle(
        publishedQuestion.choices?.[choiceIndex]?.subChoices?.[subChoiceIndex] || {},
        choiceIndex,
        subChoiceIndex,
        selectedLocale,
        publishedQuestion
      );
      return currentTitle !== publishedTitle;
    }

    // For other fields, compare directly
    const formValues = getValues() as CreateSurveyQuestionRequestData;
    const currentChoices = formValues.choices || [];
    const publishedChoices = publishedQuestion.choices || [];

    if (choiceIndex >= currentChoices.length || choiceIndex >= publishedChoices.length) {
      return false;
    }

    const currentChoice = currentChoices[choiceIndex];
    const publishedChoice = publishedChoices[choiceIndex];

    if (!currentChoice || !publishedChoice) {
      return false;
    }

    const currentSubChoices = currentChoice.subChoices || [];
    const publishedSubChoices = publishedChoice.subChoices || [];

    // If subchoices array length changed, all fields in this subchoice are considered modified
    if (currentSubChoices.length !== publishedSubChoices.length) {
      return subChoiceIndex < currentSubChoices.length;
    }

    // If subchoice at this index doesn't exist in published, it's new/modified
    if (subChoiceIndex >= publishedSubChoices.length) {
      return true;
    }

    const currentSubChoice = currentSubChoices[subChoiceIndex];
    const publishedSubChoice = publishedSubChoices[subChoiceIndex];

    if (!currentSubChoice || !publishedSubChoice) {
      return false;
    }

    // Compare the specific field
    return !isEqual(currentSubChoice[fieldName], publishedSubChoice[fieldName]);
  }

  // Common sx style for modified fields (for TextField)
  const getModifiedFieldStyle = (fieldName: keyof CreateSurveyQuestionRequestData) => ({
    '& .MuiInputBase-inputMultiline' : {
      padding: '0px',
    },
    '& .MuiInputBase-input' : {
      color: theme.palette.text.secondary,
    },
    '&.Mui-disabled': {
      color: theme.palette.action.disabled,
    },
    '& .MuiInputBase-input.Mui-disabled': {
      color: theme.palette.action.disabled,
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      
      '&.Mui-disabled': {
        color: theme.palette.action.disabled,
      }
    },
    ...(isFieldModified(fieldName) && {
      backgroundColor: (theme: any) => {
        if (theme.palette.mode === 'light') {
          return 'rgba(196, 255, 176, 0.5)';
        }
        return darken('rgba(196, 255, 176, 1)', 0.5);
      },
    }),
  });

  // Common sx style for modified Select fields
  const getModifiedSelectStyle = (fieldName: keyof CreateSurveyQuestionRequestData) => ({
    '& .MuiSelect-select' : {
      minHeight: 'unset',
      backgroundColor: 'inherit',
    },
    ...(isFieldModified(fieldName) && {
      backgroundColor: (theme: any) => {
        if (theme.palette.mode === 'light') {
          return 'rgba(196, 255, 176, 0.5)';
        }
        return darken('rgba(196, 255, 176, 1)', 0.5);
      },
    }),
  });

  // Common sx style for modified Checkbox components
  const getModifiedCheckboxStyle = (fieldName: keyof CreateSurveyQuestionRequestData) => ({
    ...(isFieldModified(fieldName) && {
      '& .MuiCheckbox-root': {
        backgroundColor: (theme: any) => {
          if (theme.palette.mode === 'light') {
            return 'rgba(196, 255, 176, 0.5)';
          }
          return darken('rgba(196, 255, 176, 1)', 0.5);
        },
      },
    }),
  });

  // Common sx style for modified choice/subchoice fields
  const getModifiedChoiceFieldStyle = (isModified: boolean) => ({
    ...(isModified && {
      '& .MuiOutlinedInput-root': {
        backgroundColor: (theme: any) => {
          if (theme.palette.mode === 'light') {
            return 'rgba(196, 255, 176, 0.5)';
          }
          return darken('rgba(196, 255, 176, 1)', 0.5);
        },
      },
    }),
  });

  // Common sx style for modified choice/subchoice Select fields
  const getModifiedChoiceSelectStyle = (isModified: boolean) => ({
    ...(isModified && {
      mt: 1,
      backgroundColor: (theme: any) => {
        if (theme.palette.mode === 'light') {
          return 'rgba(196, 255, 176, 0.5)';
        }
        return darken('rgba(196, 255, 176, 1)', 0.5);
      },
    }),
  });

  return (
    <Box component="form" ref={formRef} onSubmit={handleSubmit(onFormSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 3 }}>
      
      <Grid container spacing={2}>

        <Grid size={9}>
          <Controller
            name="title"
            control={control}
            rules={{ required: 'Question Text is required' }}
            render={({ field }) => (
              <>
                {renderFormLabel('Question Text')}
                <TextField
                  {...field}
                  multiline
                  rows={2}
                  fullWidth
                  size="small"
                  required
                  disabled={isReadOnly}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);
                    setLocaleTextValue('title', value);
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': getModifiedFieldStyle('title'),
                  }}
                />
              </>
            )}
          />
        </Grid>
      
        <Grid size={3}> 
          {/* Locale Selector */}
         {question ? <FormControl fullWidth size="small">
            {renderFormLabel('Locale')}
            <Select
              sx={{ 
                height: '36px', 
                fontSize: '12px',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '.MuiSelect-select' : {
                  minHeight: 'unset',
                  color: theme.palette.text.secondary
                }
              }}
              value={selectedLocale || CMS_DEFAULT_LOCALE}
              onChange={(e) => handleLocaleChange(e.target.value)}
            >
              {(selectedPilot!.supportedLocales as string[]).map((locale) => (
                <MenuItem key={locale} value={locale} sx={{ color: theme.palette.text.secondary }}>
                  {locales.find(l => l.code === locale)?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl> : null}
      </Grid>

      <Grid size={9}>
        {/* Info */}
        <Controller
          name="info"
          control={control}
          render={({ field }) => (
            <>
              {renderFormLabel('Info')}
              <TextField
                {...field}
                multiline
                rows={3}
                fullWidth
                size="small"
                disabled={isReadOnly}
                onChange={(e) => {
                  const value = e.target.value;
                  field.onChange(value);
                  setLocaleTextValue('info', value);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': getModifiedFieldStyle('info'),
                }}
              />
            </>
          )}
        />
      </Grid>

      <Grid size={3}> 
        {/* Verification Status */}
        {question && (
          <Controller
            name="verificationStatus"
            control={control}
            render={({ field }) => (
              <>
                {renderFormLabel('Verification Status')}
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  disabled={true}
                  value={field.value}
                />
              </>
            )}
          />
        )}
      </Grid>

      <Grid size={4}>
        {/* Question Type */}
        <Controller
          name="type"
          control={control}
          rules={{ required: 'Question Type is required' }}
          render={({ field }) => (
            <FormControl fullWidth required error={!!errors.type} disabled={isReadOnly}>
              {renderFormLabel('Question Type')}
              <Select 
                {...field} 
                sx={getModifiedSelectStyle('type')}>
                {QUESTION_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Grid>

      <Grid size={4}>
        {/* Category */}
        <Controller
          name="category"
          control={control}
          rules={{ required: 'Category is required' }}
          render={({ field }) => (
            <FormControl fullWidth required error={!!errors.category} disabled={isReadOnly}>
              {renderFormLabel('Category')}
              <Select 
                {...field} 
                sx={getModifiedSelectStyle('category')}>
                {categories.map((category) => (
                  <MenuItem key={category.categoryId} value={category.categoryId}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Grid>

      <Grid size={4}>
        {/* Group */}
        <Controller
          name="group"
          control={control}
          rules={{ required: 'Group is required' }}
          render={({ field }) => (
            <FormControl fullWidth required error={!!errors.group} disabled={isReadOnly}>
              {renderFormLabel('Group')}
              <Select 
                {...field} 
                sx={getModifiedSelectStyle('group')}>
                {GROUPS.map((group) => (
                  <MenuItem key={group} value={group}>
                    {getGroupDisplayText(group)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Grid>

      <Grid size={6}>
        {/* Profile Code */}
        <Controller
          name="profileCode"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth disabled={isReadOnly}>
              {renderFormLabel('Profile Code')}
              <Select 
                {...field} 
                value={field.value}
                sx={getModifiedSelectStyle('profileCode')}>
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {PROFILE_CODES.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Grid>

      <Grid size={6}>
        {/* State Mapper */}
        <Controller
          name="stateMapper"
          control={control}
          render={({ field }) => (
            <>
              {renderFormLabel('State Mapper')}
              <TextField
                {...field}
                fullWidth
                size="small"
                disabled={isReadOnly}
                sx={{
                  '& .MuiOutlinedInput-root': getModifiedFieldStyle('stateMapper'),
                }}
              />
            </>
          )}
        />
      </Grid>
      
    </Grid>

      {/* Fuel Types */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Fuel Type
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isGlobal}
                onChange={(e) => handleGlobalChange(e.target.checked)}
                disabled={isReadOnly}
                sx={getModifiedCheckboxStyle('fuelTypes')}
              />
            }
            label="GLOBAL"
          />
          <Box sx={{ display: 'flex', gap: 2, backgroundColor: theme.palette.background.default, borderRadius : '8px'}}>
            {FUEL_TYPES.map((fuelType) => (
              <FormControlLabel
                key={fuelType}
                control={
                  <Checkbox
                    checked={selectedFuelTypes.includes(fuelType)}
                    onChange={(e) => handleFuelTypeChange(fuelType, e.target.checked)}
                    disabled={isReadOnly || isGlobal}
                    sx={{
                      marginLeft: '10px',
                      ...getModifiedCheckboxStyle('fuelTypes')
                    }}
                  />
                }
                label={fuelType}
              />
            ))}
          </Box>
        </Box>
        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
          Select either GLOBAL or one or more specific fuel types.
        </Typography>
      </Box>

      {/* Impacts */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Impacts
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Controller
            name="impactShc"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox 
                    {...field} 
                    checked={field.value} 
                    disabled={isReadOnly}
                    sx={getModifiedCheckboxStyle('impactShc')}
                  />
                }
                label="SHC"
              />
            )}
          />
          <Controller
            name="impactItemization"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox 
                    {...field} 
                    checked={field.value} 
                    disabled={isReadOnly}
                    sx={getModifiedCheckboxStyle('impactItemization')}
                  />
                }
                label="Itemization"
              />
            )}
          />
          <Controller
            name="impactReco"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox 
                    {...field} 
                    checked={field.value} 
                    disabled={isReadOnly}
                    sx={getModifiedCheckboxStyle('impactReco')}
                  />
                }
                label="Recommendations"
              />
            )}
          />
        </Box>
      </Box>

      {/* Answer Choices */}
      {shouldShowChoices && (
        <>
          <AnswerChoicesSection
            choices={answerChoices}
            onChoicesChange={handleChoicesChange}
            questionType={questionType}
            isReadOnly={isReadOnly}
            errors={errors}
            isChoiceFieldModified={isChoiceFieldModified}
            isSubChoiceFieldModified={isSubChoiceFieldModified}
            getModifiedChoiceFieldStyle={getModifiedChoiceFieldStyle}
            getModifiedChoiceSelectStyle={getModifiedChoiceSelectStyle}
          />
          {answerChoices.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              Add answer choices
            </Typography>
          )}
        </>
      )}

    </Box>
  )
}

